/**
 * useAiAssist — generates a suggested reply draft.
 *
 * Primary path: the SAME logic the automatic Reply-Agent uses
 * (`generateAgentReply`), so "AI Assist" triggers the exact prompt built from
 * the matching agent's task + replyAgentGuidelines. It runs side-effect-free
 * (no draft record is created, the email is not marked processed).
 *
 * Fallback: if no Reply-Agent matches the email, we fall back to the generic
 * `worldEmailCreator` generation so the button always produces something.
 *
 * Either way it only fills the composer with editable text — never auto-sends.
 */

import { useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CLOUD_FUNCTION_URLS, authenticatedFetch, clientTimezone } from '../../../utils/cloudFunctions';
import { chargeCredits } from '../../../utils/creditCharge';
import { costOf } from '../../../config/creditPlans';
import { parseAddress, getPreviewText } from '../utils/emailParsing';

export default function useAiAssist({ userId, userSettings = {} }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const functions = getFunctions(undefined, 'europe-west1');

  // Generic, reply-focused generation — used only when no Reply-Agent matches.
  const generateGeneric = useCallback(async ({ originalEmail, account }) => {
    const sender = parseAddress(originalEmail.from);
    const originalText = getPreviewText(originalEmail) || originalEmail.bodyText || '';

    const payload = {
      userId,
      businessName: sender.name || 'there',
      senderName: account?.sender_name || userSettings.user_name || 'BonusClub',
      senderEmail: account?.email || '',
      recipientName: sender.name || '',
      topic: 'Reply To Mail',
      context: `You are writing a reply to the following email. Respond helpfully, naturally and concisely.\n\nOriginal subject: ${originalEmail.subject || ''}\nFrom: ${originalEmail.from || ''}\n\nOriginal message:\n${originalText}`,
      language: userSettings.language || 'de',
    };

    const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`AI generation failed: ${response.status}`);
    const data = await response.json();
    return data.emailContent || data.content || null;
  }, [userId, userSettings]);

  /**
   * @param {object} args
   * @param {object} args.originalEmail the message to reply to
   * @param {object} args.account sender account (for name/email)
   * @returns {Promise<string|null>} generated plain-text reply, or null on failure
   */
  const generateReply = useCallback(async ({ originalEmail, account }) => {
    if (!originalEmail?.id) return null;
    setGenerating(true);
    setError(null);
    try {
      // 1) Try the Reply-Agent prompt (same as the auto-responder). It charges
      //    itself server-side and refunds when no agent matches.
      try {
        const callable = httpsCallable(functions, 'generateAgentReply');
        const res = await callable({ userId, emailId: originalEmail.id, timezone: clientTimezone() });
        const out = res?.data;
        if (out?.matched && out.draftContent) {
          return out.draftContent;
        }
      } catch (agentErr) {
        // An empty balance must not fall through: the fallback below is just as
        // expensive, so it would hand out the generation for free.
        if (/resource-exhausted/.test(agentErr?.code || '')) {
          setError(agentErr?.message || 'Nicht genügend Credits für die AI-Antwort.');
          return null;
        }
        console.warn('[useAiAssist] agent reply failed, falling back:', agentErr?.message);
      }

      // 2) Fallback: generic generation. Same feature from the user's point of
      //    view, so it carries the same price — the agent path refunded itself.
      try {
        await chargeCredits({
          action: 'inbox_agent_reply',
          amount: costOf('inboxAgentReply'),
          worldId: userId,
          metadata: { emailId: originalEmail.id, path: 'generic_fallback' },
        });
      } catch (creditErr) {
        setError(
          creditErr.code === 'insufficient_credits'
            ? `Nicht genügend Credits für die AI-Antwort (${costOf('inboxAgentReply')} Credits erforderlich).`
            : 'Die Credits für die AI-Antwort konnten nicht gebucht werden — bitte erneut versuchen.'
        );
        return null;
      }
      return await generateGeneric({ originalEmail, account });
    } catch (err) {
      console.error('[useAiAssist] generation failed:', err);
      setError(err?.message || 'AI generation failed.');
      return null;
    } finally {
      setGenerating(false);
    }
  }, [userId, functions, generateGeneric]);

  return { generating, error, generateReply };
}
